import { useCallback, useState } from 'react';

import {
  applySaved,
  canSeed,
  type DraftSeed,
  isDirty,
  reseedDraft,
  seedKey,
  shouldReseed,
} from '~/lib/recordDraft';

/**
 * The editable copy of a loaded record. `draft` is null until the record arrives. It reseeds
 * when the record's id or `version` changes (a save bumps it), and keeps unsaved edits across a
 * refetch of the same version. After a save or restore call `markSaved(submitted, server?)` with the
 * draft as it was when the request went out: only fields edited since stay dirty, over the server copy
 * and the next version. Pass the query's `currentData` (never `data`, which
 * holds the previous route's record while the next loads); `recordId` double-checks the record is the route's.
 */
export function useRecordDraft<R, D>(
  record: R | undefined,
  {
    id,
    version,
    toDraft,
    recordId,
  }: {
    id: string;
    version?: string | number | null;
    toDraft: (record: R) => D;
    recordId?: (record: R) => string;
  },
) {
  const [seed, setSeed] = useState<DraftSeed<D> | null>(null);

  // Seeding during render (not in an effect) means the form never paints with empty values.
  const key = seedKey(id, version);
  if (
    record !== undefined &&
    canSeed(record, id, recordId) &&
    shouldReseed(seed?.key ?? null, key)
  ) {
    setSeed(reseedDraft(seed, id, key, toDraft(record)));
  }

  const setDraft = useCallback(
    (next: D | ((current: D) => D)) =>
      setSeed((current) =>
        current
          ? {
              ...current,
              draft: typeof next === 'function' ? (next as (c: D) => D)(current.draft) : next,
            }
          : current,
      ),
    [],
  );
  const patch = useCallback(
    (changes: Partial<D>) => setDraft((current) => ({ ...current, ...changes })),
    [setDraft],
  );
  const markSaved = useCallback(
    (submitted: D, server?: D) =>
      setSeed((current) => (current ? applySaved(current, submitted, server) : current)),
    [],
  );
  const reset = useCallback(
    () => setSeed((current) => (current ? { ...current, draft: current.saved } : current)),
    [],
  );

  // A seed from the previous route's record is not this page's draft.
  const current = seed?.id === id ? seed : null;

  return {
    draft: current?.draft ?? null,
    setDraft,
    patch,
    dirty: current ? isDirty(current.draft, current.saved) : false,
    reset,
    markSaved,
  };
}
